import { createSelector } from '@ngrx/store';
import { getLiteralsState } from 'src/app/shared/selectors/literals.selectors';
import { getRoles } from 'src/app/shared/selectors/roles.selectors';
import { getDragonflyState } from '../reducers';
import { checkbookGroupTemplateAdapter } from '../reducers/checkbook-group-template.reducer';

export const getCheckbookGroupTemplateState = createSelector(
  getDragonflyState,
  (state) => state?.checkbookGroupTemplate,
);

export const getCheckbookGroupTemplateDisplayedColumnKeys = createSelector(
  getCheckbookGroupTemplateState,
  (state) => Object.keys(state?.displayedColumns ?? {}),
);

export const getCheckbookGroupTemplateDisplayedColumns = createSelector(
  getCheckbookGroupTemplateState,
  (state) => state?.displayedColumns,
);

export const checkbookGroupTemplateSelectors = checkbookGroupTemplateAdapter.getSelectors();

export const getCheckbookGroupTemplateData = createSelector(
  getCheckbookGroupTemplateState,
  checkbookGroupTemplateSelectors.selectAll,
);

export const getCode = createSelector(getCheckbookGroupTemplateState, (state) => state?.code);

export const getCodeValidationStatus = createSelector(
  getCheckbookGroupTemplateState,
  (state) => state?.codeValidationError,
);

export const getSelectedType = createSelector(
  getCheckbookGroupTemplateState,
  (state) => state?.selectedTypeId,
);

export const getPrefix = createSelector(getCheckbookGroupTemplateState, (state) => state?.prefix);

export const getRemarks = createSelector(getCheckbookGroupTemplateState, (state) => state?.remarks);

export const getCheckbookTypes = createSelector(
  getCheckbookGroupTemplateState,
  (state) => state?.checkbooksTypes,
);

export const getCouponSet = createSelector(
  getCheckbookGroupTemplateState,
  (state) => state?.couponSet,
);

export const getExcludedGroupTemplateColKeys = createSelector(getLiteralsState, (literals) => [
  `${literals.literals['liquid.checkbook.couponset']} *`,
  `${literals.literals['liquid.checkbook.batchnumber']} *`,
  `${literals.literals['liquid.checkbook.prefix']} *`,
  'Delete',
]);

export const getSelectedCouponSet = createSelector(
  getCheckbookGroupTemplateState,
  (state) => state?.couponSetId,
);

export const getCouponBatchNumber = createSelector(
  getCheckbookGroupTemplateState,
  (state) => state?.couponBatchNumber,
);

export const getCheckbookPrefix = createSelector(
  getCheckbookGroupTemplateState,
  (state) => state?.bookCouponPrefix,
);

export const getInactive = createSelector(
  getCheckbookGroupTemplateState,
  (state) => state?.inactive,
);

export const getCheckbookGroupTemplateDataIsSubmitted = createSelector(
  getCheckbookGroupTemplateState,
  (state) => state?.isSubmittedData,
);

export const getCheckbookGroupTemplateDataAreValid = createSelector(
  getCode,
  getSelectedType,
  getPrefix,
  getCheckbookGroupTemplateData,
  getCodeValidationStatus,
  (code, selectedTypeId, prefix, data, codeValidationStatus) => {
    const isMainFormValid = code && selectedTypeId !== null && prefix && !codeValidationStatus;

    const isDataValid =
      data.length > 0
        ? data.every((row) => row.couponSetId && row.couponBatchNumber && row.bookCouponPrefix)
        : true;

    return isMainFormValid && isDataValid;
  },
);

export const getShowCodeErrorMsg = createSelector(
  getCode,
  getCheckbookGroupTemplateDataIsSubmitted,
  (code, dataIsSubmitted) => {
    if (code === '' && dataIsSubmitted) {
      return true;
    }
    return false;
  },
);

export const getShowSelectedTypeErrorMsg = createSelector(
  getSelectedType,
  getCheckbookGroupTemplateDataIsSubmitted,
  (type, dataIsSubmitted) => {
    if (!type && dataIsSubmitted) {
      return true;
    }
    return false;
  },
);

export const getShowPrefixErrorMsg = createSelector(
  getPrefix,
  getCheckbookGroupTemplateDataIsSubmitted,
  (prefix, dataIsSubmitted) => {
    if (prefix === '' && dataIsSubmitted) {
      return true;
    }
    return false;
  },
);

export const getShowCouponSetErrorMsg = createSelector(
  getCheckbookGroupTemplateData,
  getCheckbookGroupTemplateDataIsSubmitted,
  getSelectedCouponSet,
  (data, dataIsSubmitted) => {
    let checkedData: boolean[] = [];

    if (data.length > 0 && dataIsSubmitted) {
      checkedData = data.map((row) => !row.couponSetId);
    }

    return checkedData;
  },
);

export const getBatchNumberErrorMsg = createSelector(
  getCheckbookGroupTemplateData,
  getCheckbookGroupTemplateDataIsSubmitted,
  getSelectedCouponSet,
  (data, dataIsSubmitted) => {
    let checkedData: boolean[] = [];

    if (data.length > 0 && dataIsSubmitted) {
      checkedData = data.map((row) => !row.couponBatchNumber);
    }

    return checkedData;
  },
);

export const getCheckbookPrefixErrorMsg = createSelector(
  getCheckbookGroupTemplateData,
  getCheckbookGroupTemplateDataIsSubmitted,
  getSelectedCouponSet,
  (data, dataIsSubmitted) => {
    let checkedData: boolean[] = [];

    if (data.length > 0 && dataIsSubmitted) {
      checkedData = data.map((row) => !row.bookCouponPrefix);
    }

    return checkedData;
  },
);

export const getShowDeleteAllBtn = createSelector(
  getRoles,
  (roles) => !!roles?.['bookcoupongrouptemplate.create'],
);

export const getCreateNewRoleExist = createSelector(
  getRoles,
  (roles) => !!roles?.['bookcoupongrouptemplate.create'],
);

export const getIsCreateNewGroupTemplate = createSelector(
  getCheckbookGroupTemplateState,
  (state) => state?.createNewGroupTemplate,
);

export const getUpdateRoleExist = createSelector(
  getRoles,
  (roles) => !!roles?.['bookcoupongrouptemplate.update'],
);

export const getIsUpdateGroupTemplate = createSelector(
  getCheckbookGroupTemplateState,
  (state) => state?.updateGroupTemplate,
);

export const getShowAddLineBtn = createSelector(
  getCreateNewRoleExist,
  getIsCreateNewGroupTemplate,
  getUpdateRoleExist,
  getIsUpdateGroupTemplate,
  (existCreateRole, isNew, existUpdateRole, isUpdate) => {
    return (existCreateRole && isNew) || (existUpdateRole && isUpdate) ? true : false;
  },
);

export const getShowCreateNewBtn = createSelector(
  getCreateNewRoleExist,
  getIsUpdateGroupTemplate,
  (existCreateRole, isUpdate) => {
    return existCreateRole && isUpdate ? true : false;
  },
);

export const getShowPrefixPatternErrorMsg = createSelector(getPrefix, (prefix) => {
  if (prefix === '' || prefix === undefined) {
    return false;
  }

  const isValid = /^\d+$/.test(prefix);
  return isValid ? false : true;
});

export const getBookCouponGroupTemplateId = createSelector(
  getCheckbookGroupTemplateState,
  (state) => state?.bookCouponGroupTemplateId,
);
